import type { Period } from '@fd/finance-core';
import { toEngineTransaction, toSettlementRecord, type Settlement, type Transaction } from '@fd/schemas';
import { computeSettlement, type SettlementContext, type SettlementResult } from '@fd/settlement-engine';
import { readPreference } from './config.ts';

// Who owes whom, counted one way on the dashboard and on Settlements (the owner saw 323,80 £ on the
// dashboard card and 364,05 £ on Settlements: the card counted everything not settled, the page one
// month). Both follow the view last chosen on Settlements.

export type SettlementScope = 'period' | 'all';

/** The view last used on Settlements (this device): one period, or everything not settled yet. */
export function storedSettlementScope(): SettlementScope {
  return readPreference('settlementScope') === 'all' ? 'all' : 'period';
}

/**
 * A period: its own shared costs, less what an earlier settlement already covered, no transfers.
 * Everything: every shared cost against every settlement recorded.
 */
export function settlementIn(
  data: { context: SettlementContext; settlements: readonly Settlement[]; transactions: readonly Transaction[] },
  scope: SettlementScope,
  period: Pick<Period, 'start' | 'end'>,
): SettlementResult {
  const live = data.transactions.filter((tx) => tx.deletedAt === null);
  if (scope === 'all') {
    return computeSettlement(live.map(toEngineTransaction), data.settlements.filter((s) => s.deletedAt === null).map(toSettlementRecord), data.context);
  }
  const covered = new Set(data.settlements.filter((s) => s.deletedAt === null && s.status === 'SETTLED').flatMap((s) => s.items.map((i) => i.transactionId)));
  return computeSettlement(
    live.filter((tx) => tx.bookingDate >= period.start && tx.bookingDate <= period.end && !covered.has(tx.id)).map(toEngineTransaction),
    [],
    data.context,
  );
}
